import { useState, useRef } from 'react'
import type { PasswordValidationProps, RegistrationPaths } from 'login/types'
import validatePresence from '../../lib/validatePresence'
import nameFromField from '../../lib/nameFromField'
import AuthenticityToken from '../AuthenticityToken'
import ConfirmationField from '../ConfirmationField'
import HttpMethodInput from '../HttpMethodInput'
import HttpReferrerInput from '../HttpReferrerInput'
import NavigationLink from '../NavigationLink'
import PasswordTextField from '../PasswordTextField'
import SubmitButton from '../SubmitButton'
import StyledTextField from '../StyledTextField'

type RegistrationField =
  | 'current_password'
  | 'password'
  | 'password_confirmation'

type RegistrationServerErrors = { [key in RegistrationField]?: string | null }

export type Props = {
  lastPage?: string
  onSubmit: () => void
  passwordValidationProps: PasswordValidationProps
  paths: RegistrationPaths
  referrer: string
  serverErrors: RegistrationServerErrors
}

const EditRegistrationForm = ({
  lastPage = '/',
  onSubmit,
  passwordValidationProps,
  paths,
  referrer,
  serverErrors,
}: Props): JSX.Element => {
  const [errors, setErrors] = useState<RegistrationServerErrors>(
    serverErrors || {},
  )
  const currentPasswordFieldRef = useRef(null)
  const confirmationFieldRef = useRef(null)
  const passwordFieldRef = useRef(null)

  const addError = (field: RegistrationField, message: string | null) => {
    if (errors[field] !== message) {
      errors[field] = message
      setErrors(errors)
    }
  }

  const formHasErrors = () => {
    const currentPasswordError = validatePresence(
      'current password',
      /* @ts-expect-error auto-src: strict-conversion */
      currentPasswordFieldRef.current.value,
    )
    /* @ts-expect-error auto-src: strict-conversion */
    const confirmationError = confirmationFieldRef.current.errors(true)
    /* @ts-expect-error auto-src: strict-conversion */
    const isNewPasswordValid = passwordFieldRef.current.validate()

    setErrors({
      current_password: currentPasswordError,
      password_confirmation: confirmationError,
    })

    return currentPasswordError || confirmationError || !isNewPasswordValid
  }

  /* @ts-expect-error auto-src: strict-conversion */
  const handleFormSubmit = (event) => {
    if (formHasErrors()) {
      event.preventDefault()
      event.stopPropagation()
    } else {
      onSubmit()
    }
  }

  /* @ts-expect-error auto-src: strict-conversion */
  const handleRequiredFieldChange = (event) => {
    const { value } = event.target

    if (value) {
      const name = nameFromField(event.target)
      const error = validatePresence(name, value)

      addError(name, error)
    }
  }

  /* @ts-expect-error auto-src: strict-conversion */
  const handleConfirmationValidation = (error) => {
    addError('password_confirmation', error)
  }

  /* @ts-expect-error auto-src: strict-conversion */
  const handlePasswordChange = (event) => {
    /* @ts-expect-error auto-src: strict-conversion */
    confirmationFieldRef.current.handleOriginalFieldChange(event)
  }

  return (
    <form
      action={paths.update}
      autoComplete="off"
      method="post"
      onSubmit={handleFormSubmit}
      role="form"
    >
      <AuthenticityToken />
      <HttpMethodInput />
      <HttpReferrerInput referrer={referrer} />
      <StyledTextField
        FormHelperTextProps={{
          error: Boolean(errors.current_password),
          'data-testid': 'current-password-helper-text',
        }}
        helperText={errors.current_password}
        inputProps={{ 'data-testid': 'current-password-field' }}
        inputRef={currentPasswordFieldRef}
        label="Current password"
        name="user[current_password]"
        onBlur={handleRequiredFieldChange}
        onChange={handleRequiredFieldChange}
        type="password"
      />
      <PasswordTextField
        ref={passwordFieldRef}
        initialError={errors.password}
        label="New password"
        name="user[password]"
        onChange={handlePasswordChange}
        {...passwordValidationProps}
      />
      <ConfirmationField
        ref={confirmationFieldRef}
        FormHelperTextProps={{
          error: Boolean(errors.password_confirmation),
          /* @ts-expect-error: MUI does not accept data-testid */
          'data-testid': 'password-confirmation-helper-text',
        }}
        helperText={errors.password_confirmation}
        humanIdentifier="password confirmation"
        inputProps={{ 'data-testid': 'password-confirmation-field' }}
        label="Password confirmation"
        name="user[password_confirmation]"
        onValidate={handleConfirmationValidation}
        originalFieldPlural="passwords"
        type="password"
      />
      <SubmitButton label="CHANGE YOUR PASSWORD" />
      <NavigationLink href={lastPage}>Back</NavigationLink>
    </form>
  )
}

export default EditRegistrationForm
